import { useFetcher } from '@remix-run/react';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Admin } from '~/interfaces/admin';
import type { Post } from '~/interfaces/post';
import type { Subscription } from '~/interfaces/subscriptions';

import type { PostLike } from '~/hooks/useLoaders/usePostInsights';

import LoginOrSubscribe from '../CTAs/LoginOrSubscribe';
import HeartFilledIcon from '../Icons/HeartFilledIcon';
import HeartOutlineIcon from '../Icons/HeartOutlineIcon';
import SlideUpModal from '../Modal/SlideUpModal';

interface Props {
  post: Post;
  subscription?: Subscription;
  likes: PostLike[];
  fetched: boolean;
  admin?: Admin;
  className?: string;
}

const LikeCounter = ({
  children,
  count = 0,
  className,
}: {
  children: React.ReactNode;
  count?: number;
  className?: string;
}) => {
  return (
    <div className={cx(className, 'flex items-center')}>
      {children}

      <span
        className={cx(
          count === 0 ? 'hidden text-transparent' : 'text-[#6B7280] text-wt-text-on-background',
          'text-sm font-medium group-hover:opacity-100',
        )}
      >
        {count}
      </span>
    </div>
  );
};

const getIsLiked = (likes: PostLike[], admin?: Admin, subscription?: Subscription): boolean => {
  if (likes.length === 0) {
    return false;
  }

  if (!admin && !subscription) {
    return false;
  }

  return likes
    .filter((like) => like.liked === true)
    .some((like) => like.user_id === admin?.id || like.subscription_id === subscription?.id);
};

const PostLikeAction = ({ post, subscription, admin, className, likes, fetched }: Props) => {
  const { t } = useTranslation();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetcher = useFetcher();
  const isAdminLoggedIn = Boolean(admin);
  const isSubcriberLoggedIn = Boolean(subscription);
  const isNotLoggedIn = !isAdminLoggedIn && !isSubcriberLoggedIn;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Leaving this for backwards compatibility
  useEffect(() => {
    if (fetched && !hasMounted) {
      setLiked(getIsLiked(likes, admin, subscription));
      setLikeCount(likes.filter((like) => like.liked === true).length);
      setHasMounted(true);
    }
  }, [fetched, hasMounted]);

  const updateAttributionInState = () => {
    if (liked) {
      setLiked(false);
      setLikeCount(likeCount - 1);
    } else {
      setLiked(true);
      setLikeCount(likeCount + 1);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    if (isAdminLoggedIn) {
      fetcher.submit(formData, {
        method: 'post',
        action: '/admins/post_like',
      });
    } else {
      fetcher.submit(formData, { method: 'post', action: '/post_like' });
    }

    updateAttributionInState();
  };

  if (isNotLoggedIn) {
    return (
      <>
        <button className="group" type="button" onClick={() => setIsModalOpen(true)}>
          <LikeCounter count={likeCount} className="text-wt-text-on-background opacity-50 group-hover:opacity-100">
            <div className="rounded-full p-1 hover:bg-[#f3f4f6]">
              <HeartOutlineIcon className={cx(className, 'h-4 w-4 stroke-1')} />
            </div>
          </LikeCounter>
        </button>
        {isModalOpen && (
          <SlideUpModal
            title={t('gated.login_or_subscribe')}
            description=""
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          >
            <div>
              <LoginOrSubscribe />
            </div>
          </SlideUpModal>
        )}
      </>
    );
  }

  return (
    <fetcher.Form
      method="post"
      onSubmit={handleSubmit}
      action={isAdminLoggedIn ? '/admins/post_like' : '/post_like'}
      className="group relative top-[3px]"
    >
      <input type="hidden" name="post_id" value={post.id} />
      <input type="hidden" name="subscription_id" value={subscription?.id} />
      <input type="hidden" name="user_id" value={admin?.id} />
      <button type="submit">
        {liked ? (
          <LikeCounter count={likeCount}>
            <div className="rounded-full p-1 hover:bg-[#f3f4f6]">
              <HeartFilledIcon className={cx(className, 'fill-wt-primary text-wt-primary', 'h-4 w-4 stroke-1')} />
            </div>
          </LikeCounter>
        ) : (
          <LikeCounter count={likeCount} className="text-wt-text-on-background opacity-50 group-hover:opacity-100">
            <div className="rounded-full p-1 hover:bg-[#f3f4f6]">
              <HeartOutlineIcon className={cx(className, 'h-4 w-4 stroke-1')} />
            </div>
          </LikeCounter>
        )}
      </button>
    </fetcher.Form>
  );
};

export default PostLikeAction;
